import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment
} from '@material-ui/core'
import { Close, Save } from '@material-ui/icons'
import TextField from '../components/TextField.js'
import redux from '../redux/index.js'
import { readFileAttachments } from '../lib/office.js'
import { cleanFileName, formatResourceString } from '../util.js'
import { ERROR_ACTION } from '../redux/error/actions.js'
import {
  CONTENT_BACKGROUND,
  FOOTER_BACKGROUND,
  FOOTER_HEIGHT,
  HEADER_BACKGROUND,
  HEADER_HEIGHT
} from '../styles.js'

const FILE_PANE_SET_TOP_HEIGHT_EVENT = 'pds-file-pane-set-top-height'
const PANE_SEPARATOR_HEIGHT = 3
const MIN_PANE_HEIGHT = 140

const RenameAttachmentsDialog = ({
  strings,
  siteItem,
  initialFileItem,
  selectedFileNode,
  fileTreeItems,
  preloadedAttachments,
  dropInfo,
  onHideDialog,
  onReadAttachmentsError,
  onSaveAttachments
}) => {
  const [attachments, setAttachments] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [paneHeight, setPaneHeight] = useState(0)
  const [paneTop, setPaneTop] = useState(0)
  const [resizeState, setResizeState] = useState({ active: false, startY: 0, startHeight: 0, totalHeight: 0 })
  const firstAttachmentInputRef = useRef(null)

  useEffect(() => {
    let mounted = true

    if (Array.isArray(preloadedAttachments)) {
      const normalized = preloadedAttachments.map((attachment) => {
        const originalFileName = String(attachment.fileName || '')
        const cleanedFileName = cleanFileName(originalFileName)
        const { baseName, extension } = splitFileNameAndExtension(cleanedFileName)
        return {
          fileData: attachment.fileData,
          originalFileName,
          fileName: composeFileNameWithExtension(baseName, extension),
          editableName: baseName,
          fileExtension: extension,
          documentInformation: '',
          handled: false,
          uploadedToPath: '',
          selected: true
        }
      })
      setAttachments(normalized)
      setLoading(false)
      return () => {
        mounted = false
      }
    }

    readFileAttachments((err, result) => {
      if (!mounted) return

      if (err) {
        onReadAttachmentsError(err)
        return
      }

      const normalized = Array.isArray(result)
        ? result.map((attachment) => {
          const originalFileName = String(attachment.fileName || '')
          const cleanedFileName = cleanFileName(originalFileName)
          const { baseName, extension } = splitFileNameAndExtension(cleanedFileName)
          return {
            fileData: attachment.fileData,
            originalFileName,
            fileName: composeFileNameWithExtension(baseName, extension),
            editableName: baseName,
            fileExtension: extension,
            documentInformation: '',
            handled: false,
            uploadedToPath: '',
            selected: true
          }
        })
        : []

      setAttachments(normalized)
      setLoading(false)
    })

    return () => {
      mounted = false
    }
  }, [onReadAttachmentsError, preloadedAttachments])

  useEffect(() => {
    if (loading || attachments.length === 0) {
      return
    }

    const id = window.requestAnimationFrame(() => {
      const input = firstAttachmentInputRef.current
      if (!input || typeof input.focus !== 'function') {
        return
      }

      input.focus()
      const valueLength = String(input.value || '').length
      if (typeof input.setSelectionRange === 'function') {
        input.setSelectionRange(valueLength, valueLength)
      }
    })

    return () => {
      window.cancelAnimationFrame(id)
    }
  }, [loading, attachments.length])

  useEffect(() => {
    const syncPaneHeight = () => {
      const dbView = document.getElementById('db-view-wrapper')
      if (!dbView) {
        return
      }

      const rect = dbView.getBoundingClientRect()
      const nextHeight = Number(dbView.clientHeight) || 0
      if (nextHeight > 0) {
        setPaneHeight(nextHeight)
      }
      setPaneTop(Math.max(0, Number(rect.top) || 0))
    }

    syncPaneHeight()

    const dbView = document.getElementById('db-view-wrapper')
    if (!dbView || typeof window.ResizeObserver === 'undefined') {
      return
    }

    const observer = new window.ResizeObserver(() => {
      syncPaneHeight()
    })
    observer.observe(dbView)

    const onWindowResize = () => {
      syncPaneHeight()
    }
    window.addEventListener('resize', onWindowResize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', onWindowResize)
    }
  }, [])

  useEffect(() => {
    if (!resizeState.active) {
      return
    }

    const onMouseMove = (event) => {
      const diffY = Number(event.pageY) - resizeState.startY
      const maxPaneHeight = Math.max(MIN_PANE_HEIGHT, resizeState.totalHeight - PANE_SEPARATOR_HEIGHT - MIN_PANE_HEIGHT)
      const nextHeight = clampNumber(resizeState.startHeight + diffY, MIN_PANE_HEIGHT, maxPaneHeight)
      setPaneHeight(nextHeight)
      window.dispatchEvent(new window.CustomEvent(FILE_PANE_SET_TOP_HEIGHT_EVENT, {
        detail: { topHeight: nextHeight }
      }))
    }

    const onMouseUp = () => {
      setResizeState((prev) => ({ ...prev, active: false }))
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)

    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [resizeState])

  const selectedIsFolder = selectedFileNode && (selectedFileNode.type === 'folder' || selectedFileNode.type === 'rootfolder')
  const targetFolder = selectedIsFolder ? selectedFileNode : initialFileItem
  const targetIsFolder = targetFolder && (targetFolder.type === 'folder' || targetFolder.type === 'rootfolder')
  const targetFolderPath = getTargetFolderRelativePath(targetFolder)
  const targetFolderDisplay = targetFolderPath || targetFolder?.name || strings.no_options
  const existingTargetFileNames = getExistingTargetFileNameSet(targetFolder, fileTreeItems)

  const buildDuplicateNameSet = () => {
    const counts = attachments.reduce((acc, attachment) => {
      if (!attachment.selected || attachment.handled) {
        return acc
      }
      const key = (attachment.fileName || '').trim().toLowerCase()
      if (key.length > 0) {
        acc[key] = (acc[key] || 0) + 1
      }
      return acc
    }, {})

    return new Set(Object.keys(counts).filter((key) => counts[key] > 1))
  }

  const duplicateNames = buildDuplicateNameSet()
  const hasDuplicateNames = duplicateNames.size > 0
  const selectedAttachments = attachments.filter((attachment) => attachment.selected && !attachment.handled)
  const selectableAttachments = attachments.filter((attachment) => !attachment.handled)
  const disableSave = loading ||
    saving ||
    !targetIsFolder ||
    attachments.length === 0 ||
    selectedAttachments.length === 0 ||
    hasDuplicateNames ||
    selectedAttachments.some((attachment) => attachment.fileName.length === 0)

  const onFileNameChange = (index, value) => {
    setAttachments((prev) => {
      const next = [...prev]
      const baseName = cleanFileName(value)
      const extension = next[index].fileExtension
      next[index] = {
        ...next[index],
        editableName: baseName,
        fileName: composeFileNameWithExtension(baseName, extension)
      }
      return next
    })
  }

  const onAttachmentToggle = (index, checked) => {
    setAttachments((prev) => {
      const next = [...prev]
      if (next[index].handled) {
        return next
      }
      next[index] = {
        ...next[index],
        selected: checked
      }
      return next
    })
  }

  const onAttachmentDocumentInformationChange = (index, value) => {
    setAttachments((prev) => {
      const next = [...prev]
      next[index] = {
        ...next[index],
        documentInformation: String(value || '')
      }
      return next
    })
  }

  const allSelected = selectableAttachments.length > 0 && selectableAttachments.every((attachment) => attachment.selected)
  const anySelected = selectableAttachments.some((attachment) => attachment.selected)
  const partiallySelected = anySelected && !allSelected

  const onToggleSelectAll = (checked) => {
    setAttachments((prev) => prev.map((attachment) => {
      if (attachment.handled) {
        return attachment
      }
      return {
        ...attachment,
        selected: checked
      }
    }))
  }

  const onSaveSelectedAttachments = () => {
    if (disableSave) {
      return
    }

    const selectedIndexes = attachments.reduce((acc, attachment, index) => {
      if (attachment.selected && !attachment.handled) {
        acc.push(index)
      }
      return acc
    }, [])

    if (selectedIndexes.length === 0) {
      return
    }

    const selectedIndexSet = new Set(selectedIndexes)
    const uploadedToPath = targetFolderPath || targetFolder?.name || strings.no_options

    setSaving(true)
    const result = onSaveAttachments(siteItem, targetFolder, selectedAttachments, () => {
      setAttachments((prev) => prev.map((attachment, index) => {
        if (!selectedIndexSet.has(index)) {
          return attachment
        }

        return {
          ...attachment,
          selected: false,
          handled: true,
          uploadedToPath
        }
      }))
    })

    Promise.resolve(result).finally(() => {
      setSaving(false)
    })
  }

  const onResizeHandleMouseDown = (event) => {
    event.preventDefault()
    const metrics = getPaneLayoutMetrics()
    if (!metrics) {
      return
    }

    const currentTopHeight = paneHeight > 0 ? paneHeight : metrics.topHeight

    setResizeState({
      active: true,
      startY: Number(event.pageY),
      startHeight: currentTopHeight,
      totalHeight: metrics.totalHeight
    })
  }

  const getPartialMatchMessage = () => {
    const requested = Number(dropInfo?.requestedCount || 0)
    const matched = Number(dropInfo?.matchedCount || 0)
    if (requested > 0 && matched >= 0 && matched < requested) {
      return strings.advanced_upload_partial_match
        .replace('%1', String(matched))
        .replace('%2', String(requested))
    }
    return ''
  }

  const renderContent = () => {
    if (loading) {
      return <div>{strings.loading}</div>
    }

    if (attachments.length === 0) {
      return <div>{strings.no_options}</div>
    }

    return (
      <>
        {getPartialMatchMessage() ? <div style={matchInfoStyle}>{getPartialMatchMessage()}</div> : null}
        <div style={selectionActionsStyle}>
          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={allSelected}
                indeterminate={partiallySelected}
                onChange={(event) => onToggleSelectAll(event.target.checked)}
                color='primary'
                disabled={selectableAttachments.length === 0}
              />
            }
            label={<span style={selectAllLabelStyle}>{strings.select_all_none}</span>}
            style={selectAllControlStyle}
          />
        </div>
        {attachments.map((attachment, index) => {
          const normalizedName = (attachment.fileName || '').trim().toLowerCase()
          const isHandled = !!attachment.handled
          const hasDuplicate = attachment.selected && !isHandled && normalizedName.length > 0 && duplicateNames.has(normalizedName)
          const hasTargetNameConflict = !isHandled && normalizedName.length > 0 && existingTargetFileNames.has(normalizedName)
          const helperText = isHandled
            ? (strings.uploaded_to_folder || '').replace('%1', attachment.uploadedToPath || strings.no_options)
            : (hasDuplicate ? strings.attachment_name_duplicate : (hasTargetNameConflict ? strings.attachment_name_exists_in_target : ''))
          const extensionInputProps = attachment.fileExtension
            ? {
                endAdornment: (
                  <InputAdornment position='end'>
                    <span style={extensionTextStyle}>{attachment.fileExtension}</span>
                  </InputAdornment>
                )
              }
            : undefined
          return (
            <div key={`attachment-${index}`} style={attachmentRowStyle}>
              <Checkbox
                size='small'
                checked={!!attachment.selected}
                onChange={(event) => onAttachmentToggle(index, event.target.checked)}
                color='primary'
                style={attachmentCheckboxStyle}
                disabled={isHandled || saving}
              />
              <div style={attachmentFieldsStyle}>
                <TextField
                  required={attachment.selected}
                  label={attachment.originalFileName || formatResourceString(strings.attachment_name_with_index || '%1 %2', strings.attachment_name, index + 1)}
                  value={attachment.editableName || ''}
                  error={hasDuplicate}
                  helperText={helperText}
                  style={attachmentNameStyle(attachment.selected)}
                  onChange={(event) => onFileNameChange(index, event.target.value)}
                  InputLabelProps={{ shrink: true, style: inputLabelStyle }}
                  InputProps={extensionInputProps}
                  FormHelperTextProps={{ style: helperTextStyle({ isHandled, isWarning: hasTargetNameConflict && !hasDuplicate }) }}
                  inputProps={{ style: inputTextStyle }}
                  inputRef={index === 0 ? firstAttachmentInputRef : null}
                  autoFocus={index === 0}
                  disabled={!attachment.selected || isHandled || saving}
                />
                <TextField
                  label={strings.pdscol_document_information || 'Document Information'}
                  value={attachment.documentInformation || ''}
                  style={attachmentMetadataStyle(attachment.selected)}
                  onChange={(event) => onAttachmentDocumentInformationChange(index, event.target.value)}
                  InputLabelProps={{ shrink: true, style: inputLabelStyle }}
                  inputProps={{ style: inputTextStyle }}
                  disabled={!attachment.selected || isHandled || saving}
                />
              </div>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <Dialog
      open
      hideBackdrop
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      style={dialogRootStyle}
      PaperProps={{ style: dialogPaperStyle(paneHeight, paneTop) }}
    >
      <DialogTitle style={dialogTitleStyle}>
        {strings.save_attachments_to_folder || strings.rename_attachments_dialog_title}
      </DialogTitle>
      <DialogContent style={contentStyle}>
        <div style={targetFolderStyle}>
          {targetFolderDisplay}
        </div>
        {renderContent()}
      </DialogContent>
      <DialogActions style={dialogActionsStyle}>
        <Button onClick={onHideDialog} style={actionButtonStyle()}>
          <Close style={actionIconStyle} />
          {strings.close}
        </Button>
        <Button
          disabled={disableSave}
          onClick={onSaveSelectedAttachments}
          style={actionButtonStyle(disableSave)}
        >
          <Save style={actionIconStyle} />
          {strings.save_to_folder || strings.save}
        </Button>
      </DialogActions>
      <div style={resizeHandleStyle} onMouseDown={onResizeHandleMouseDown} />
    </Dialog>
  )
}

function getPaneLayoutMetrics () {
  const dbView = document.getElementById('db-view-wrapper')
  const fileView = document.getElementById('file-view-wrapper')
  if (!dbView || !fileView) {
    return null
  }

  const topHeight = Number(dbView.clientHeight) || 0
  const bottomHeight = Number(fileView.clientHeight) || 0
  return {
    topHeight,
    bottomHeight,
    totalHeight: topHeight + bottomHeight + PANE_SEPARATOR_HEIGHT
  }
}

function clampNumber (value, min, max) {
  return Math.max(min, Math.min(max, Number(value) || 0))
}

const contentStyle = {
  background: CONTENT_BACKGROUND,
  paddingTop: 12,
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 12,
  fontSize: '0.9rem',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0
}

const dialogRootStyle = {
  pointerEvents: 'none'
}

const dialogPaperStyle = (paneHeight, paneTop) => {
  const safeHeight = Number(paneHeight) > 0 ? Number(paneHeight) : undefined
  const safeTop = Number(paneTop) > 0 ? Number(paneTop) : 0
  const dialogHeight = typeof safeHeight === 'number' ? safeHeight + safeTop : undefined
  return {
    pointerEvents: 'auto',
    margin: 0,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    maxWidth: '100%',
    height: dialogHeight,
    maxHeight: dialogHeight,
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }
}

const dialogTitleStyle = {
  background: HEADER_BACKGROUND,
  color: 'white',
  padding: '0px 19px',
  fontSize: '20px',
  height: HEADER_HEIGHT,
  display: 'flex',
  alignItems: 'center'
}

const resizeHandleStyle = {
  height: 8,
  cursor: 'ns-resize',
  background: '#d2dce3',
  borderTop: '1px solid #bfcad3',
  borderBottom: '1px solid #bfcad3'
}

const dialogActionsStyle = {
  borderTop: '1px solid white',
  background: FOOTER_BACKGROUND,
  height: FOOTER_HEIGHT,
  padding: 0
}

const actionIconStyle = {
  marginRight: '5px',
  width: 18,
  height: 18
}

const actionButtonStyle = (disabled = false) => {
  return {
    color: disabled ? '#aaafb2' : 'white',
    textTransform: 'none',
    marginRight: '10px'
  }
}

const targetFolderStyle = {
  fontSize: '0.86rem',
  lineHeight: 1.3,
  color: '#2b3f4c',
  background: '#f4f7f9',
  border: '1px solid #d4dde3',
  borderRadius: 4,
  padding: '8px 10px',
  marginBottom: 10,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}

const attachmentRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: 8
}

const attachmentCheckboxStyle = {
  marginRight: 4,
  padding: 4,
  marginTop: 10
}

const attachmentFieldsStyle = {
  flex: 1,
  minWidth: 0
}

const attachmentNameStyle = (enabled) => {
  return {
    background: 'white',
    opacity: enabled ? 1 : 0.6,
    marginBottom: 0
  }
}

const attachmentMetadataStyle = (enabled) => {
  return {
    background: 'white',
    opacity: enabled ? 1 : 0.6,
    marginTop: 4,
    marginBottom: 0
  }
}

const selectionActionsStyle = {
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 0
}

const selectAllControlStyle = {
  marginTop: 0,
  marginBottom: 0,
  marginLeft: -2
}

const selectAllLabelStyle = {
  fontSize: '0.86rem'
}

const inputLabelStyle = {
  fontSize: '0.86rem'
}

const inputTextStyle = {
  fontSize: '0.9rem',
  lineHeight: 1.2,
  paddingTop: 8,
  paddingBottom: 8,
  paddingLeft: 8,
  paddingRight: 8
}

const helperTextStyle = ({ isHandled, isWarning }) => {
  return {
    marginTop: 1,
    marginBottom: 0,
    fontSize: '0.78rem',
    lineHeight: 1.2,
    color: isHandled ? '#2f6f4f' : (isWarning ? '#9a6400' : undefined)
  }
}

const extensionTextStyle = {
  fontSize: '0.82rem',
  color: '#51606d',
  marginLeft: 4
}

const matchInfoStyle = {
  color: '#1f2f3d',
  background: '#f3e7cd',
  border: '1px solid #d6ba7a',
  borderRadius: 3,
  padding: '5px 7px',
  marginBottom: 6,
  fontSize: '0.82rem',
  lineHeight: 1.25
}

const mapStateToProps = (state) => {
  const selectedContext = state.selected.current
  const treeId = selectedContext ? `${selectedContext.type}:${selectedContext.id}` : null
  const fileTreeItems = treeId ? (state.files.trees?.[treeId]?.items || []) : []

  return {
    strings: state.locale.strings,
    siteItem: state.dialog.meta?.siteItem,
    initialFileItem: state.dialog.meta?.fileItem,
    selectedFileNode: state.files.selectedFileNode,
    fileTreeItems,
    preloadedAttachments: state.dialog.meta?.attachments,
    dropInfo: state.dialog.meta?.dropInfo
  }
}

function getExistingTargetFileNameSet (targetFolder, treeItems) {
  const folder = resolveTargetFolderNode(targetFolder, treeItems)
  const children = Array.isArray(folder?.children) ? folder.children : []
  const names = children
    .filter((item) => item?.type === 'file')
    .map((item) => String(item?.name || '').trim().toLowerCase())
    .filter(Boolean)
  return new Set(names)
}

function resolveTargetFolderNode (targetFolder, treeItems) {
  if (!targetFolder) {
    return null
  }

  if (!Array.isArray(treeItems) || treeItems.length === 0) {
    return targetFolder
  }

  const targetId = targetFolder.id
  const targetPath = targetFolder.path

  const byId = targetId ? findTreeNodeById(treeItems, targetId) : null
  if (byId) {
    return byId
  }

  const byPath = targetPath ? findTreeNodeByPath(treeItems, targetPath) : null
  if (byPath) {
    return byPath
  }

  return targetFolder
}

function findTreeNodeById (items, id) {
  const queue = Array.isArray(items) ? [...items] : []
  while (queue.length > 0) {
    const current = queue.shift()
    if (current?.id === id) {
      return current
    }
    if (Array.isArray(current?.children) && current.children.length > 0) {
      queue.push(...current.children)
    }
  }
  return null
}

function findTreeNodeByPath (items, path) {
  const queue = Array.isArray(items) ? [...items] : []
  const target = normalizePath(path)
  while (queue.length > 0) {
    const current = queue.shift()
    if (normalizePath(current?.path) === target) {
      return current
    }
    if (Array.isArray(current?.children) && current.children.length > 0) {
      queue.push(...current.children)
    }
  }
  return null
}

function normalizePath (value) {
  return String(value || '').replace(/^\/+/, '').replace(/\/+$/, '').toLowerCase()
}

function getTargetFolderRelativePath (item) {
  if (!item) {
    return ''
  }

  if (typeof item.path === 'string' && item.path.length > 0) {
    return normalizeDocumentLibraryPath(item.path)
  }

  const value = item.serverRelativeUrl || item.uri
  if (typeof value !== 'string' || value.length === 0) {
    return ''
  }

  try {
    const pathname = value.startsWith('http') ? new URL(value).pathname : value
    return normalizeDocumentLibraryPath(decodeURIComponent(pathname))
  } catch (err) {
    return normalizeDocumentLibraryPath(String(value))
  }
}

function normalizeDocumentLibraryPath (value) {
  const trimmed = String(value || '').replace(/^\/+/, '')
  if (!trimmed) {
    return ''
  }

  // Strip known doc-library roots like files/ or files_<id>/ for compact display.
  return trimmed.replace(/^files(?:_[^/]+)?\/?/i, '')
}

function splitFileNameAndExtension (fileName) {
  const full = String(fileName || '')
  const lastDot = full.lastIndexOf('.')
  if (lastDot <= 0 || lastDot === full.length - 1) {
    return {
      baseName: full,
      extension: ''
    }
  }

  return {
    baseName: full.slice(0, lastDot),
    extension: full.slice(lastDot)
  }
}

function composeFileNameWithExtension (baseName, extension) {
  const safeBase = String(baseName || '')
  if (!safeBase.length) {
    return ''
  }

  return `${safeBase}${String(extension || '')}`
}

const mapDispatchToProps = (dispatch) => {
  const { dialog, files, save } = redux.actions
  return {
    onHideDialog: () => {
      dispatch(dialog.hideDialog())
    },
    onReadAttachmentsError: (err) => {
      dispatch(dialog.hideDialog())
      dispatch({
        type: ERROR_ACTION,
        errorType: 'ERROR_TYPE_FILE_READ_ATTACHMENTS',
        errorMessage: err.message
      })
    },
    onSaveAttachments: (siteItem, fileItem, attachments, onUploaded) => {
      const uploadAttachments = attachments.map((attachment) => {
        const baseName = cleanFileName(attachment.editableName || '')
        const extension = attachment.fileExtension || ''
        const documentInformation = String(attachment.documentInformation || '').trim()
        const metadataFields = documentInformation.length > 0
          ? { pdscol_document_information: documentInformation }
          : undefined
        return {
          ...attachment,
          fileName: composeFileNameWithExtension(baseName, extension),
          metadataFields
        }
      })

      return dispatch(save.uploadRenamedFileAttachments(siteItem, fileItem, uploadAttachments, () => {
        if (typeof onUploaded === 'function') {
          onUploaded()
        }
        const { type } = fileItem
        if (type === 'rootfolder') {
          dispatch(files.getRootFolder(siteItem))
        } else if (type === 'folder') {
          dispatch(files.getSubFolder(siteItem, fileItem))
        }
      }))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RenameAttachmentsDialog)
